package one.healthium.data.user

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.functions.FirebaseFunctions
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.shareIn
import kotlinx.coroutines.launch
import one.healthium.base.coroutines.IO
import one.healthium.data.user.dto.PushTokenDto
import one.healthium.data.user.storage.FirebaseAvatarStorage
import one.healthium.data.user.storage.FirebaseUserStorage
import one.healthium.data.user.storage.LocalUserStorage
import one.healthium.data.user.utils.signInWithOptions
import one.healthium.domain.health_log.models.HealthLogOption
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.UserRepository
import one.healthium.domain.user.isLocalId
import one.healthium.domain.user.models.FoodOptions
import one.healthium.domain.user.models.SignInOptions
import one.healthium.domain.user.models.SymptomsLogOptions
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.UserProfileOptions
import one.healthium.healthKit.data.HealthKitHelper
import one.healthium.locale.LocaleProvider
import one.primehealth.app.AppRestartController
import kotlin.time.Instant

@OptIn(ExperimentalCoroutinesApi::class)
internal class AppUserRepository(
    private val auth: FirebaseAuth,
    private val functions: FirebaseFunctions,
    private val localUserStorage: LocalUserStorage,
    private val remoteUserStorage: FirebaseUserStorage,
    private val healthKitHelper: HealthKitHelper,
    private val avatarStorage: FirebaseAvatarStorage,
    private val appRestartController: AppRestartController,
    private val localeProvider: LocaleProvider,
) : UserRepository {

    private val logger = Logger.withTag("AppUserRepository (${this.hashCode()})")

    private val scope = CoroutineScope(Dispatchers.IO + SupervisorJob())

    private val userFlow = combine(
        localUserStorage.get(),
        auth.authStateChanged
    ) { user, fbUser ->
        logger.d("local user: $user, fbUser: ${fbUser?.uid}")
        val result = if (user == null) {
            logger.d("local user is null for some reasons, try to fetch Firebase user")
            val remoteUser = if (fbUser != null) {
                safeFetchRemote(fbUser.uid)
            } else {
                null
            }
            if (remoteUser != null) {
                localUserStorage.set(remoteUser)
            } else {
                localUserStorage.set(UserModel.createDefault())
            }
            user
        } else if (user.isLocalId.not() && fbUser == null) {
            logger.d("user has remote id but not authenticated, create new local user")
            val newUser = UserModel.createDefault()
            localUserStorage.set(newUser)
            newUser
        } else {
            user
        }
        result
    }
        .filterNotNull()
        .shareIn(scope, started = SharingStarted.Lazily, replay = 1)

    init {
        // monitor remote user
        scope.launch {
            auth.authStateChanged
                .flatMapLatest { fbUser ->
                    logger.d("auth state changed, fbUser: ${fbUser?.uid}")
                    if (fbUser != null) {
                        combine(
                            remoteUserStorage.get(fbUser.uid),
                            localUserStorage.get()
                        ) { remoteUser, localUser ->
                            if (localUser?.id == remoteUser?.id) { // ждем пока либо signIn либо syncLocalToRemoteUser обновят локального пользователя
                                remoteUser
                            } else {
                                null
                            }
                        }
                            .catch {
                                logger.e("sync error, fbUser: ${fbUser.uid}", it)
                            }
                    } else {
                        emptyFlow()
                    }
                }
                .filterNotNull()
                .catch {
                    logger.e("user watcher error", it)
                }
                .distinctUntilChanged()
                .collect { user ->
                    logger.d("sync remote-to-local user: $user")
                    localUserStorage.set(user)
                }
        }
    }

    override suspend fun signIn(options: SignInOptions): UserModel {
        val result = auth.signInWithOptions(options)
        logger.d("sign in result: ${result.user?.uid}")
        val userId = result.user?.uid
        if (userId != null) {
            val remoteUser = remoteUserStorage.get(userId).firstOrNull()
            logger.d("remote user: $remoteUser")
            return if (remoteUser != null) {
                notifyLogin(remoteUser.profile.email)
                localUserStorage.set(remoteUser)
                remoteUser
            } else {
                logger.d("remote user not found, create default user")
                val defaultUser = UserModel.createDefault(userId)
                localUserStorage.set(defaultUser)
                defaultUser
            }
        } else {
            throw Exception("user not found")
        }
    }

    override suspend fun requestMagicLink(email: String) {
        val locale = localeProvider.getLocale()
        functions.httpsCallable("send_magic_link_email")
            .invoke(mapOf("email" to email, "locale" to locale))
    }

    override fun getUser(): Flow<UserModel> {
        return userFlow
    }

    override suspend fun updateUserProfile(data: UserProfileModel) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.updateProfile(data)
            if (userId.isLocalId().not()) {
                remoteUserStorage.updateProfile(userId, data)
            }
        }
    }

    override suspend fun updateUserProfile(options: UserProfileOptions) {
        val userId = currentLocalUserId()
        if (userId != null) {
            if (userId.isLocalId().not()) {
                remoteUserStorage.updateProfile(userId, options)
            }
        }
    }

    override suspend fun addWaterIntake(value: Float) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.addWaterIntake(value)
            if (userId.isLocalId().not()) {
                remoteUserStorage.addWaterIntake(userId, value)
            }
            healthKitHelper.logWater(value)
        }
    }

    override suspend fun addWeight(value: Float) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.updateWeight(value)
            if (userId.isLocalId().not()) {
                remoteUserStorage.addWeight(userId, value)
            }
            healthKitHelper.logWeight(value)
        }
    }

    override suspend fun setWeightForDate(date: Instant, value: Float) {
        val userId = currentLocalUserId()
        if (userId != null) {
            localUserStorage.setWeightForDate(date, value)
            if (userId.isLocalId().not()) {
                remoteUserStorage.setWeightForDate(userId, date, value)
            }
        }
    }

    override suspend fun removeWeight(date: Instant) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.removeWeight(userId, date)
        }
    }

    override suspend fun addFood(options: FoodOptions) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.addFood(userId, options)
        }
    }

    override suspend fun setFoodForDate(dayId: String, foodId: String, options: FoodOptions) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.setFoodForDate(userId, dayId, foodId, options)
        }
    }

    override suspend fun removeFood(dayId: String, foodId: String) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.removeFood(userId, dayId, foodId)
        }
    }

    override suspend fun setPeriodData(data: List<Instant>) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.setPeriods(userId, data)
        }
    }

    override suspend fun syncLocalToRemoteUser() {
        logger.d("sync local to remote user")
        val localUser = localUserStorage.get().firstOrNull()
        val remoteUserId = if (localUser == null || localUser.isLocalId) {
            getOrCreateRemoteUserId()
        } else {
            logger.d("sync local to remote user: skip, already synced")
            return
        }
        val remoteUser = if (localUser != null) {
            val updatedRemoteUser = remoteUserStorage.update(remoteUserId, localUser)
            if (localUser.profile.weight != null) {
                remoteUserStorage.addWeight(remoteUserId, localUser.profile.weight)
            }
            updatedRemoteUser
        } else {
            logger.d("no local user, no need to sync")
            remoteUserStorage.get(remoteUserId).firstOrNull()
        }
        logger.d("force sync remote user: $remoteUser")
        if (remoteUser != null) {
            localUserStorage.set(remoteUser)
        } else {
            logger.d("remote user is null, create default local user with remote id")
            val updatedLocalUser = localUser?.copy(id = remoteUserId) ?: UserModel.createDefault(remoteUserId)
            localUserStorage.set(updatedLocalUser)
        }
    }

    override suspend fun deleteAccount() {
        functions.httpsCallable("deleteUser").invoke()
    }

    override suspend fun logout() {
        auth.signOut()
        localUserStorage.clear()
    }

    override suspend fun syncHealthData(fromDate: Instant) {
        syncWeight(fromDate)
    }

    override suspend fun updatePlayTime(status: Int) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.updatePlayTime(userId, status)
        }
    }

    override suspend fun addSymptoms(options: SymptomsLogOptions) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.addSymptoms(userId, options)
        }
    }

    override suspend fun addHealthLog(options: List<HealthLogOption>) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            remoteUserStorage.addHealthLog(userId, options)
        }
    }

    override suspend fun setAvatar(image: ByteArray) {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            avatarStorage.uploadAvatar(userId, image).let { url ->
                remoteUserStorage.updateProfile(userId, UserProfileOptions(avatarUrl = url))
            }
        }
    }

    override suspend fun deleteAvatar() {
        val userId = currentLocalUserId()
        if (userId != null && userId.isLocalId().not()) {
            avatarStorage.deleteAvatar(userId)
            remoteUserStorage.updateProfile(userId, UserProfileOptions(avatarUrl = null))
        }
    }

    override suspend fun savePushToken(token: String, platform: String, installationId: String) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            val dto = PushTokenDto(token, platform, installationId, Timestamp.now())
            remoteUserStorage.savePushToken(userId, dto)
        }
    }

    override suspend fun removePushToken(token: String) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.removePushToken(userId, token)
        }
    }

    override suspend fun updateLocale(locale: String) {
        val userId = currentLocalUserId()
        if (userId != null && !userId.isLocalId()) {
            remoteUserStorage.updateLocale(userId, locale)
        }
    }

    private suspend fun syncWeight(fromDate: Instant) {
        healthKitHelper.getWeightFromDate(fromDate).forEach { record ->
            setWeightForDate(record.date, record.weight)
        }
    }

    private suspend fun safeFetchRemote(userId: String): UserModel? {
        return try {
            logger.d("authenticated userId: $userId")
            remoteUserStorage.get(userId).firstOrNull()
        } catch (e: Exception) {
            logger.e("failed sync user", e)
            null
        }
    }

    private suspend fun currentLocalUserId(): String? {
        return localUserStorage.get().firstOrNull()?.id
    }

    private suspend fun getOrCreateRemoteUserId(): String {
        return auth.currentUser?.uid ?: auth.signInAnonymously().user?.uid ?: throw Exception("can't register user")
    }

    private fun notifyLogin(email: String?) {
        if (email.isNullOrBlank()) return

        scope.launch {
            try {
                functions.httpsCallable("reminders_app_installed")
                    .invoke(mapOf("email" to email))
            } catch (e: Exception) {
                logger.e("failed to invoke reminders_app_installed", e)
            }
        }
    }
}
